import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from '../../../components/atoms/page';
import { FetchFromBackoffice } from '../../../utils/requests';
import { Vehicle } from '../../../types/vehicle';
import TableWidget, { TableColumn } from '../../../components/molecules/tableWidget';
import { formatDateTime } from '../../../components/timeAgo';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { Spacing } from '../../../constants/theme';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelectedWorkshop } from '../../../context/workshopContext';

type TransferLogPosition = {
  latitude: number;
  longitude: number;
};

type TransferLogEntry = {
  transferredBy: string;
  userName: string | null;
  workshopId: string;
  vin: string;
  startAddress: string;
  endAddress: string;
  startPosition: TransferLogPosition;
  endPosition: TransferLogPosition;
  startTimestamp: string;
  endTimestamp: string | null;
};

type TransferLogRow = {
  id: string;
  vin: string;
  userName: string;
  startAddress: string;
  endAddress: string;
  startTimestamp: string;
  endTimestamp: string | null;
};

const Transfer = () => {
  const { isLoading } = useAuth0();
  const navigate = useNavigate();
  const { selectedOrganisationId: workshopId, isLoading: isLoadingWorkshops } = useSelectedWorkshop();
  const [transferLogs, setTransferLogs] = useState<TransferLogEntry[]>();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [showOngoing, setShowOngoing] = useState<boolean>(false)
  const [isLoadingLogs, setIsLoadingLogs] = useState(true)

  useEffect(() => {
    if (!workshopId) {
      if (!isLoadingWorkshops) {
        setIsLoadingLogs(false);
      }
      return;
    }

    const fetchTransferLogs = async () => {
      setIsLoadingLogs(true);
      try {
        const data = await FetchFromBackoffice<TransferLogEntry[]>(
          `portal/workshops/${workshopId}/transfer-log`
        );
        setTransferLogs(data);
      } catch (error) {
        console.log('error with fetching transfer logs', error);
      } finally {
        setIsLoadingLogs(false);
      }
    };

    const fetchVehicles = async () => {
      try {
        const data = await FetchFromBackoffice<Vehicle[]>(
          `portal/workshops/${workshopId}/vehicles/checkedin`
        );
        setVehicles(data ?? []);
      } catch (error) {
        console.log('error with fetching vehicles', error);
      }
    };

    fetchTransferLogs();
    fetchVehicles();
  }, [workshopId, isLoadingWorkshops]);

  const sessionIdByVin = useMemo(() => {
    const map = new Map<string, string>();
    for (const vehicle of vehicles) {
      if (vehicle.workshopSessionId) {
        map.set(vehicle.vin, vehicle.workshopSessionId);
      }
    }
    return map;
  }, [vehicles]);

  const tableData = useMemo<TransferLogRow[]>(
    () =>
      (transferLogs ?? []).map((entry) => ({
        id: `${entry.vin}-${entry.startTimestamp}`,
        vin: entry.vin,
        userName: entry.userName ?? entry.transferredBy,
        startAddress: entry.startAddress,
        endAddress: entry.endAddress,
        startTimestamp: entry.startTimestamp,
        endTimestamp: entry.endTimestamp,
      })).filter(entry => showOngoing ? !entry.endTimestamp : true),
    [transferLogs, showOngoing]
  );

  const columns = useMemo<TableColumn[]>(
    () => [
      { id: 'vin', label: 'VIN' },
      { id: 'userName', label: 'Transferred by', minWidth: 160 },
      { id: 'startAddress', label: 'From' },
      { id: 'endAddress', label: 'To' },
      {
        id: 'startTimestamp',
        label: 'Started',
        minWidth: 160,
        format: (value) => formatDateTime(String(value ?? '')) ?? '-',
      },
      {
        id: 'endTimestamp',
        label: 'Ended',
        minWidth: 160,
        format: (value) => formatDateTime(String(value ?? '')) ?? '-',
      },
    ],
    []
  );

  if (isLoading) {
    return <Box>Loading ...</Box>;
  }

  return (
    <Page title="Transfer log">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: Spacing.gap,
          mt: Spacing.gap,
        }}
      >
        <FormControlLabel
          sx={{ cursor: 'pointer', marginLeft: 0 }}
          control={
            <Checkbox
              checked={showOngoing}
              onChange={(event) => setShowOngoing(event.target.checked)}
              sx={{ cursor: 'pointer' }}
            />
          }
          label="Show only ongoing"
        />
      </Box>
      <TableWidget
        data={tableData}
        columns={columns}
        defaultSort="startTimestampdesc"
        onClickRow={(_event, _id, row) => {
          const vin = (row as TransferLogRow).vin;
          const workshopSessionId = sessionIdByVin.get(vin);
          if (!workshopSessionId) {
            return;
          }
          navigate(`/vehicle/${vin}/${workshopSessionId}`);
        }}
        themes={{ device: 'desktop' }}
        pagination
        loadingStatus={isLoadingLogs ? 'loading' : undefined}
      />
    </Page>
  );
};

export default Transfer;
